import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { DropdownMenuItem } from './Dropdown.component';
import './DropDownMenu.component.scss';

export type DropDownMenuProps = {
  isActive: boolean;
  itemList: DropdownMenuItem[];
  onChange?: (item: DropdownMenuItem) => void;
};

const DropDownMenu = ({ isActive, itemList = [], onChange }: DropDownMenuProps) => {
  const [animationState, setAnimationState] = useState<'idle' | 'opening' | 'active' | 'closing'>('idle');
  const [position, setPosition] = useState<{ alignRight: boolean }>({ alignRight: false });
  const menuRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    if (isActive) {
      if (menuRef.current) {
        const menuRect = menuRef.current.getBoundingClientRect();
        const parentRect = menuRef.current.parentElement?.getBoundingClientRect();
        
        if (parentRect) {
          const viewportWidth = window.innerWidth;
          
          const spaceOnRight = viewportWidth - parentRect.right;
          const alignRight = spaceOnRight < menuRect.width;
          
          setPosition({ alignRight });
        }
      }
      
      setAnimationState('opening');
      timerRef.current = setTimeout(() => {
        setAnimationState('active');
      }, 220); 
    } else if (animationState !== 'idle') {
      setAnimationState('closing');
      timerRef.current = setTimeout(() => {
        setAnimationState('idle');
      }, 150);
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isActive]);

  const menuClass = `ui-dropdown-menu ${animationState === 'opening' ? 'opening active' : ''} ${animationState === 'active' ? 'active' : ''} ${animationState === 'closing' ? 'closing' : ''} ${position.alignRight ? 'align-right' : ''}`;
  
  return (
    <div ref={menuRef} className={menuClass}>
      {
        itemList.map((item, i) => {
          const { path, color, icon, name, onClick, disabled } = item;
          
          const itemClass = `ui-dropdown-menu__item ${disabled ? 'disabled' : ''}`;
          
          if (path && !disabled) {
            return (
              <Link to={path} key={i} target={item.target}>
                <div className={itemClass}>
                  <div className="ui-dropdown-menu__item__icon" style={{ color: color }}>
                    {icon}
                  </div>
                  <div className="ui-dropdown-menu__item__text">
                    {name}
                  </div>
                </div>
              </Link>
            );
          }

          return (
            <div
              role="button"
              tabIndex={disabled ? -1 : 0}
              key={i}
              className={itemClass}
              onClick={() => {
                if (disabled) return;
                if (onClick) {
                  onClick();
                  // Also call onChange to close dropdown
                  if (onChange) onChange(item);
                  return;
                }
                if (onChange) return onChange(item);
              }}
              onKeyDown={(e) => {
                if (disabled) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (onClick) return onClick();
                  if (onChange) return onChange(item);
                }
              }}
            >
              <div className="ui-dropdown-menu__item__icon" style={{ color: color }}>
                {icon}
              </div>
              <div className="ui-dropdown-menu__item__text">
                {name}
              </div>
            </div>
          );
        })
      }
    </div>
  );
};

export default DropDownMenu;
